import React, { useState, useEffect } from 'react';
import { authAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import Icon from '../../components/common/Icon';
import Button from '../../components/common/Button';
import toast from 'react-hot-toast';

const MediatorSettings = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    fullName: '',
    email: '',
    mobile: '',
  });

  const [security, setSecurity] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [payout, setPayout] = useState({
    upiId: '',
    bankName: '',
    accountHolderName: '',
    accountNumber: '',
    ifscCode: '',
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await authAPI.getProfile();
        const userData = response.data?.data || response.data;
        
        if (userData) {
          setProfile({
            fullName: userData.username || userData.fullName || '',
            email: userData.email || '',
            mobile: userData.phoneNumber || userData.mobile || '',
          });
          
          setPayout({
            upiId: userData.upiId || '',
            bankName: userData.bankName || '',
            accountHolderName: userData.accountHolderName || '',
            accountNumber: userData.bankAccount || '',
            ifscCode: userData.ifscCode || '',
          });
        }
      } catch (error) {
        console.error('Failed to fetch profile:', error);
        toast.error('Failed to load profile data');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Consolidate all fields for the profile update API
      const updateData = {
        username: profile.fullName,
        phoneNumber: profile.mobile,
        upiId: payout.upiId,
        bankName: payout.bankName,
        bankAccount: payout.accountNumber,
        ifscCode: payout.ifscCode
      };
      
      console.log('Updating profile with data:', updateData);
      await authAPI.updateProfile(updateData);
      toast.success('Settings updated successfully');
    } catch (error) {
      console.error('Failed to update settings:', error);
      toast.error(error.response?.data?.message || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSave = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!security.currentPassword) {
      toast.error('Current password is required');
      return;
    }
    if (!security.newPassword) {
      toast.error('New password is required');
      return;
    }
    if (security.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }
    if (security.newPassword !== security.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setSaving(true);
    try {
      // Using the resetPassword API as requested
      // The API expects: email, newPassword, confirmPassword, role
      const role = user?.role?.toUpperCase() || 'MEDIATOR';
      
      await authAPI.resetPassword(
        profile.email,
        security.newPassword,
        security.confirmPassword,
        role
      );
      
      toast.success('Password updated successfully');
      setSecurity({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      console.error('Failed to update password:', error);
      toast.error(error.response?.data?.message || 'Failed to update password');
    } finally {
      setSaving(false);
    }
  };

  const handlePayoutSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // API call to update payout info
      const payoutData = {
        upiId: payout.upiId,
        bankName: payout.bankName,
        accountHolderName: payout.accountHolderName,
        bankAccount: payout.accountNumber,
        ifscCode: payout.ifscCode
      };
      await authAPI.updateProfile(payoutData);
      toast.success('Payout details updated successfully');
    } catch (error) {
      console.error('Failed to update payout details:', error);
      toast.error(error.response?.data?.message || 'Failed to update payout details');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Icon name="refresh-cw" size={32} className="animate-spin" color="var(--primary)" />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', flex: 1, backgroundColor: '#f8fafc', overflow: 'auto' }}>
      <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
        <h1 style={{ fontSize: '28px', fontWeight: '800', color: '#1e293b', marginBottom: '24px' }}>Account Settings</h1>

        <div style={{ display: 'grid', gap: '24px' }}>
          {/* Profile Section */}
          <div style={{ background: '#fff', borderRadius: '16px', padding: '24px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h2 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '20px' }}>Profile Information</h2>
            <form onSubmit={handleProfileSave}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>Full Name</label>
                  <input 
                    type="text" 
                    value={profile.fullName} 
                    onChange={(e) => setProfile({...profile, fullName: e.target.value})}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>Email</label>
                  <input 
                    type="email" 
                    value={profile.email} 
                    disabled
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', backgroundColor: '#f3f4f6' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>Mobile Number</label>
                  <input 
                    type="tel" 
                    value={profile.mobile} 
                    onChange={(e) => setProfile({...profile, mobile: e.target.value})}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db' }}
                  />
                </div>
              </div>
              <Button type="submit" loading={saving}>Save Changes</Button>
            </form>
          </div>

          {/* Payout Section */}
          <div style={{ background: '#fff', borderRadius: '16px', padding: '24px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h2 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '20px' }}>Payout Details</h2>
            <form onSubmit={handleProfileSave}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', marginBottom: '20px' }}>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>UPI ID</label>
                  <input 
                    type="text" 
                    value={payout.upiId} 
                    onChange={(e) => setPayout({...payout, upiId: e.target.value})}
                    placeholder="example@upi"
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>Bank Name</label>
                  <input 
                    type="text" 
                    value={payout.bankName} 
                    onChange={(e) => setPayout({...payout, bankName: e.target.value})}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>Account Number</label>
                  <input 
                    type="text" 
                    value={payout.accountNumber} 
                    onChange={(e) => setPayout({...payout, accountNumber: e.target.value})}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>IFSC Code</label>
                  <input 
                    type="text" 
                    value={payout.ifscCode} 
                    onChange={(e) => setPayout({...payout, ifscCode: e.target.value})}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db' }}
                  />
                </div>
              </div>
              <Button type="submit" loading={saving} variant="primary" style={{ backgroundColor: '#10b981' }}>Save Changes</Button>
            </form>
          </div>

          {/* Security Section */}
          <div style={{ background: '#fff', borderRadius: '16px', padding: '24px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h2 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '20px' }}>Security</h2>
            <form onSubmit={handlePasswordSave}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>Current Password</label>
                  <input 
                    type="password" 
                    value={security.currentPassword} 
                    onChange={(e) => setSecurity({...security, currentPassword: e.target.value})}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>New Password</label>
                  <input 
                    type="password" 
                    value={security.newPassword} 
                    onChange={(e) => setSecurity({...security, newPassword: e.target.value})}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>Confirm New Password</label>
                  <input 
                    type="password" 
                    value={security.confirmPassword} 
                    onChange={(e) => setSecurity({...security, confirmPassword: e.target.value})}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db' }}
                  />
                </div>
              </div>
              <Button type="submit" loading={saving} variant="secondary">Update Password</Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MediatorSettings;
